import {useTranslate} from '@tolgee/react'
import {ReactNode} from 'react'
import * as z from 'zod'

const parsedType = (data: unknown): string => {
  const t = typeof data

  switch (t) {
    case 'number': {
      return Number.isNaN(data) ? 'NaN' : 'number'
    }
    case 'object': {
      if (Array.isArray(data)) {
        return 'array'
      }
      if (data === null) {
        return 'null'
      }

      if (Object.getPrototypeOf(data) !== Object.prototype && data?.constructor) {
        return data.constructor.name
      }
    }
  }
  return t
}

export function ZodLocales(props: {children: ReactNode}) {
  const {t} = useTranslate('validation')

  const error: () => z.core.$ZodErrorMap = () => {
    const Sizable: Record<string, {unit: string; verb: string}> = {
      string: {unit: t('characters', 'characters'), verb: t('to_have', 'to have')},
      file: {unit: t('bytes', 'bytes'), verb: t('to_have', 'to have')},
      array: {unit: t('items', 'items'), verb: t('to_have', 'to have')},
      set: {unit: t('items', 'items'), verb: t('to_have', 'to have')},
    }

    function getSizing(origin: string): {unit: string; verb: string} | null {
      return Sizable[origin] ?? null
    }

    const Nouns: {
      [k in z.core.$ZodStringFormats | (string & {})]?: string
    } = {
      regex: 'input',
      email: 'email address',
      url: 'URL',
      emoji: 'emoji',
      uuid: 'UUID',
      uuidv4: 'UUIDv4',
      uuidv6: 'UUIDv6',
      nanoid: 'nanoid',
      guid: 'GUID',
      cuid: 'cuid',
      cuid2: 'cuid2',
      ulid: 'ULID',
      xid: 'XID',
      ksuid: 'KSUID',
      datetime: 'ISO datetime',
      date: 'ISO date',
      time: 'ISO time',
      duration: 'ISO duration',
      ipv4: 'IPv4 address',
      ipv6: 'IPv6 address',
      cidrv4: 'IPv4 range',
      cidrv6: 'IPv6 range',
      base64: 'base64-encoded string',
      base64url: 'base64url-encoded string',
      json_string: 'JSON string',
      e164: 'E.164 number',
      jwt: 'JWT',
      template_literal: 'input',
    }

    return issue => {
      const value = issue.value || issue.input
      const isEmpty =
        !value || (typeof value === 'string' && !value.trim()) || (Array.isArray(value) && value.length === 0)

      switch (issue.code) {
        case 'invalid_type': {
          // a required field left empty (undefined) must read as required, not as a type mismatch
          if (isEmpty) {
            return t('required', 'This field is required.')
          }

          return t('invalid_type', `Received {received} but {expected} was expected.`, {
            expected: t(`type_${issue.expected}`, issue.expected),
            received: t(`type_${parsedType(issue.input)}`, parsedType(issue.input)),
          })
        }

        case 'invalid_value': {
          if (isEmpty) {
            return t('required', 'This field is required.')
          }

          if (issue.values.length === 1) {
            return t('invalid_value', 'Unexpected value was provided, expected: {expected}.', {
              expected: z.util.stringifyPrimitive(issue.values[0]),
            })
          }

          return t('invalid_option', 'Selected value must be one of: {options}.', {
            options: z.util.joinValues(issue.values, ', '),
          })
        }

        case 'too_big': {
          const adj = issue.inclusive ? '<=' : '<'
          const sizing = getSizing(issue.origin)
          if (sizing) {
            return t('too_many_elements', 'Too many {unit}. (maximum is {max})', {
              value: issue.origin || t('value', 'value'),
              max: `${adj}${issue.maximum.toString()}`,
              unit: sizing.unit || t('elements', 'elements'),
            })
          }

          return t('too_big', 'Maximum value should be {max}.', {
            value: issue.origin || t('value', 'value'),
            max: `${adj}${issue.maximum.toString()}`,
          })
        }

        case 'too_small': {
          const adj = issue.inclusive ? '>=' : '>'
          const sizing = getSizing(issue.origin)

          if (isEmpty) {
            return t('required', 'This field is required.')
          }

          if (issue.origin !== 'string' && sizing) {
            return t('too_few_elements', `Please select a value.`, {
              value: issue.origin || t('value', 'value'),
              min: `${adj}${issue.minimum.toString()}`,
              unit: sizing.unit || t('elements', 'elements'),
            })
          }

          return t('too_small', 'Minimum value should be {min}.', {
            value: issue.origin || t('value', 'value'),
            min: `${adj}${issue.minimum.toString()}`,
          })
        }

        case 'invalid_format': {
          const _issue = issue as z.core.$ZodStringFormatIssues

          if ('inst' in _issue) {
            const instance = _issue.inst as z.ZodUUID
            const isRequired = Boolean(instance.minLength)

            if (isRequired && isEmpty) {
              return t('required', 'This field is required.')
            }
          }

          if (_issue.format === 'starts_with') {
            return t('invalid_string_prefix', 'Input must start with "{prefix}".', {
              prefix: _issue.prefix,
            })
          }
          if (_issue.format === 'ends_with') {
            return t('invalid_string_suffix', 'Input must end with "{suffix}".', {
              suffix: _issue.suffix,
            })
          }
          if (_issue.format === 'includes') {
            return t('invalid_string_content', 'Input must include "{includes}".', {
              includes: _issue.includes,
            })
          }
          if (_issue.format === 'regex') {
            return t('invalid_string_regex', 'Input must match pattern {pattern}.', {
              pattern: _issue.pattern,
            })
          }

          return t('invalid_string_format', 'Invalid {format}.', {
            format: Nouns[_issue.format] ?? issue.format,
          })
        }

        case 'not_multiple_of': {
          return t('number_not_multiple', 'Provided value is not a multiple of {divisor}.', {
            divisor: issue.divisor,
          })
        }

        case 'unrecognized_keys': {
          return t('unrecognized_keys', 'Unrecognized {length, plural, one {key} other {keys}}: {values}.', {
            length: issue.keys.length,
            values: z.util.joinValues(issue.keys, ', '),
          })
        }

        case 'invalid_key': {
          return t('invalid_key', 'Invalid key in {origin}.', {
            origin: t(issue.origin, issue.origin),
          })
        }

        case 'invalid_union': {
          return t('invalid_union', 'Invalid input.')
        }

        case 'invalid_element': {
          return t('invalid_element', 'Invalid value in {origin}.', {
            origin: t(issue.origin, issue.origin),
          })
        }
        case 'custom': {
          const tKey = issue.params?.tKey
          if (tKey) {
            return t(tKey, tKey)
          }
          return t('invalid_fallback', 'Invalid input.')
        }

        default: {
          return t('invalid_fallback', 'Invalid input.')
        }
      }
    }
  }

  z.config({
    localeError: error(),
  })

  return props.children
}
